<script setup lang="ts">
// Privacy policy page — required as a public URL by Meta when configuring
// Lead Ad forms (https://developers.facebook.com/docs/marketing-api/guides/lead-ads),
// and by GDPR/152-ФЗ for the EU/RU traffic that contacts us through the
// site. Content is intentionally plain prose so legal review is easy and
// the page is robot-readable without rich blocks.
//
// Section list mirrors a standard privacy notice for a single-owner
// vacation-rental business: who we are, what we collect, why, how long,
// who else sees it, your rights, cookies, version. All text comes from
// i18n keys so the same page renders in en/ru/ka.

const { t, locale } = useI18n();
const route = useRoute();

const canonicalUrl = computed(() => `https://georgiadream.ge${route.path}`);

const ogLocale = computed(() =>
  locale.value === "ru" ? "ru_RU" : locale.value === "ka" ? "ka_GE" : "en_US",
);
const ogLocaleAlternate = computed(() =>
  ["en_US", "ru_RU", "ka_GE"].filter((l) => l !== ogLocale.value),
);

useSeoMeta({
  title: () => t("privacy.seo.title"),
  description: () => t("privacy.seo.description"),
  ogTitle: () => t("privacy.seo.title"),
  ogDescription: () => t("privacy.seo.description"),
  ogType: "website",
  ogUrl: canonicalUrl,
  ogLocale,
  ogLocaleAlternate,
  // robots: this page must remain crawlable so Meta + search engines
  // can verify it. No noindex.
});

useHead({
  link: [{ rel: "canonical", href: canonicalUrl }],
});

useBreadcrumbsLd(() => [
  { name: t("nav.home"), path: "/" },
  { name: t("privacy.title"), path: "/privacy" },
]);

// Sections rendered in fixed order. Localised copy comes from
// i18n/locales/{en,ru,ka}/common.json under `privacy.sections.*`.
const SECTION_KEYS = [
  "controller",
  "data",
  "purposes",
  "lawfulBasis",
  "retention",
  "sharing",
  "rights",
  "cookies",
  "security",
  "changes",
  "contact",
] as const;
</script>

<template>
  <div>
    <section
      class="relative flex min-h-[40vh] w-full items-center overflow-hidden py-10 text-white sm:py-14 md:min-h-[52vh] md:py-20"
      aria-labelledby="privacy-hero-title"
    >
      <NuxtImg
        src="/images/complex/complex3.jpg"
        :alt="t('privacy.heroImageAlt')"
        loading="eager"
        fetchpriority="high"
        format="webp"
        sizes="sm:100vw md:100vw lg:1920px"
        class="absolute inset-0 h-full w-full object-cover"
      />
      <div
        aria-hidden="true"
        class="absolute inset-0 bg-gradient-to-b from-slate-900/60 via-slate-900/70 to-slate-900/90"
      />
      <div
        aria-hidden="true"
        class="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(212,175,55,0.18),transparent_55%)]"
      />
      <div class="container relative z-10">
        <p
          class="font-body text-xs uppercase tracking-[0.22em] text-amber-300/80"
        >
          {{ t("privacy.eyebrow") }}
        </p>
        <h1
          id="privacy-hero-title"
          class="mt-3 font-display text-3xl italic leading-tight text-balance text-white drop-shadow sm:text-4xl md:text-5xl lg:text-6xl"
        >
          {{ t("privacy.title") }}
        </h1>
        <p
          class="mt-4 max-w-3xl text-base text-white/90 sm:text-lg md:mt-6 md:text-xl"
        >
          {{ t("privacy.intro") }}
        </p>
        <p
          class="mt-4 font-body text-xs uppercase tracking-[0.18em] text-white/60"
        >
          {{ t("privacy.lastUpdated") }}
        </p>
      </div>
    </section>

    <div
      aria-hidden="true"
      class="pointer-events-none h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent"
    />

    <section class="bg-white py-12 dark:bg-slate-900 md:py-16">
      <div class="container">
        <div
          class="prose prose-slate mx-auto max-w-3xl dark:prose-invert prose-headings:font-display prose-headings:italic prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-3 prose-p:text-base prose-p:leading-relaxed"
        >
          <article
            v-for="key in SECTION_KEYS"
            :key="key"
          >
            <h2>{{ t(`privacy.sections.${key}.heading`) }}</h2>
            <p style="white-space: pre-line">
              {{ t(`privacy.sections.${key}.body`) }}
            </p>
          </article>
        </div>
      </div>
    </section>
  </div>
</template>
